import * as React from "react";
import { Button, Tag } from "antd";
import { CloseOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp } from "react-feather";
import { NotificationItem as NotificationItemType } from "../../store/notificationDrawerSlice";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

interface Props {
  item: NotificationItemType;
  onRemove: (id: string) => void;
}

export const NotificationItem: React.FC<Props> = ({ item, onRemove }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hasDetails = item.details !== undefined && item.details !== null;

  const timeAgo = dayjs(item.timestamp).fromNow();

  // Truncate page path for display
  const truncatedPage = React.useMemo(() => {
    const parts = item.page.split("/").filter(Boolean);
    if (parts.length <= 4) return item.page;
    return ".../" + parts.slice(-3).join("/");
  }, [item.page]);

  return (
    <div
      className="p-3 mb-2 rounded-md border transition-colors"
      style={{
        backgroundColor: "var(--color-drop)",
        borderColor: "var(--color-border)",
      }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {item.success ? (
            <CheckCircleOutlined style={{ color: "var(--color-action)", fontSize: 16 }} />
          ) : (
            <CloseCircleOutlined style={{ color: "var(--color-danger)", fontSize: 16 }} />
          )}
          <span className="font-medium truncate" style={{ color: "var(--color-default)" }}>
            {item.action}
          </span>
        </div>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined style={{ fontSize: 12 }} />}
          onClick={() => onRemove(item.id)}
          className="opacity-50 hover:opacity-100"
        />
      </div>

      {/* Message */}
      <div className="text-sm mb-2" style={{ color: "var(--color-default)" }}>
        {item.message}
      </div>

      {/* Meta row */}
      <div className="flex items-center justify-between text-xs" style={{ color: "var(--color-label)" }}>
        <span title={item.page}>{truncatedPage}</span>
        <span>{timeAgo}</span>
      </div>

      {/* Details section */}
      {hasDetails && (
        <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--color-border)" }}>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 text-xs cursor-pointer bg-transparent border-0 p-0"
            style={{ color: "var(--color-primary)" }}
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {isExpanded ? "Hide details" : "Show details"}
          </button>
          {isExpanded && (
            <pre
              className="mt-2 p-2 rounded text-xs overflow-auto max-h-40"
              style={{
                backgroundColor: "var(--color-gray-1400)",
                color: "var(--color-default)",
              }}
            >
              {typeof item.details === "string"
                ? item.details
                : JSON.stringify(item.details, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};
